import { useDebounce, useTranslate } from 'ur-react-hooks'
import { ActionButton, Input, TableFiltering, TableMenu } from 'components'
import { Filtering } from 'components/Table/TableFiltering'
import { ShallowProject } from 'modules/projects/types.graphql'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { usePermission } from 'util/hooks'
import { PERMISSIONS } from 'util/permissions'
import { DeviationStatusType } from '../components'
import { ShallowDeviation, ShallowDeviationCategory } from '../types.graphql'
import {
  getCategoriesFiltering,
  getProjectsFiltering,
  getStatusFiltering,
} from '../util'
import { DeviationCard } from './DeviationCard'

const Wrapper = styled.div`
  div.no-deviations {
    display: flex;
    justify-content: center;
    padding: 3rem 0;
  }
`
const Toolbar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 24px;
  align-items: center;

  padding: 0.5rem 1rem 0;

  input {
    border-right-width: 0px;
  }
  .--input-icon-right {
    transition: border-color 0.1s linear;
  }
  &:focus-within {
    .--input-icon-right {
      border-color: ${props => props.theme.colors.primary};
    }
  }
`

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`

interface MobileDeviationsProps {
  deviations: ShallowDeviation[]
  projects?: ShallowProject[]
  categories: ShallowDeviationCategory[]

  isInternal?: boolean
  hideProject?: boolean
  initialStatuses?: DeviationStatusType[]

  search: string
  makeMenu: (deviation: ShallowDeviation) => TableMenu

  onCreateNewDeviation: () => void
  onSearchChange: (value: string) => void
  onFilterChange: (value: TableFiltering) => void
}

export const MobileDeviations: React.FC<MobileDeviationsProps> = ({
  deviations,
  projects,
  categories,

  isInternal = false,
  hideProject = false,
  initialStatuses = ['underTreatment'],

  search,
  makeMenu,

  onCreateNewDeviation,
  onSearchChange,
  onFilterChange,
}) => {
  const translations = useTranslate({
    search: 'deviations.search',
    noData: 'common.no-data',
    noResults: 'common.no-results',

    statuses: {
      archived: 'common.archived',
      closed: 'common.closed',
      underTreatment: 'common.under-treatment',
    },
  })

  const canCreateDeviation = usePermission(
    PERMISSIONS.deviations.add.deviation,
    PERMISSIONS.projects.view.project
  )

  const debouncedSearch = useDebounce(search)

  const projectsFilter = useMemo(
    () => (!!projects ? getProjectsFiltering(projects) : null),
    [projects]
  )
  const statusFilter = useMemo(
    () => getStatusFiltering(translations.statuses, initialStatuses),
    [translations.statuses, initialStatuses]
  )
  const categoriesFilter = useMemo(
    () => getCategoriesFiltering(categories),
    [categories]
  )

  return (
    <Wrapper>
      {canCreateDeviation && <ActionButton onClick={onCreateNewDeviation} />}

      <Toolbar>
        <Input
          value={search}
          placeholder={translations.search}
          iconRightProps={{
            icon: 'search',
            type: 'solid',
            color: 'gray4',
            size: '18px',
            translateX: '-2px',
          }}
          height="56px"
          onChange={onSearchChange}
        />

        <FilterWrapper>
          {!!projectsFilter && (
            <Filtering
              id="projectName"
              config={projectsFilter}
              icon={{
                icon: 'folder-open',
                size: '1rem',
              }}
              onFilterChange={value => onFilterChange(value)}
            />
          )}

          <Filtering
            id="categories"
            config={categoriesFilter}
            icon={{
              icon: 'th-large',
              size: '1rem',
            }}
            onFilterChange={value => onFilterChange(value)}
          />

          <Filtering
            id="status"
            config={statusFilter}
            icon={{
              size: '1rem',
            }}
            onFilterChange={value => onFilterChange(value)}
          />
        </FilterWrapper>
      </Toolbar>

      {deviations.map(deviation => (
        <DeviationCard
          key={deviation.id}
          deviation={deviation}
          isInternal={isInternal}
          hideProject={hideProject}
          editDeleteMenu={makeMenu(deviation)}
        />
      ))}

      {!deviations?.length && (
        <div className="no-deviations">
          {!!debouncedSearch ? translations.noResults : translations.noData}
        </div>
      )}
    </Wrapper>
  )
}
